import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  ApiReportResponse,
  FailedMeasure,
  TabularDataResponse,
} from "../../../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext";
import { selectReportType } from "../../../../store/selectors/fieldsStateSelectors.ts";
import {
  createApiReportAction,
  createCurrentReportAction,
  createDevToolsAction,
} from "../../../../store/utils/storeUtils";
import { isApiReportResponse } from "../../../../utilities/isApiReportResponse.ts";
import type { ReducerAction } from "../../../viewer/tabular/storage/gridDataState.ts";
import { GridDataStateActionType } from "../../../viewer/tabular/storage/gridDataState.ts";
import { useIsAddingFields } from "../../common/fields/useFieldDestination.ts";
import useDeferredBuild from "../../common/hooks/useDeferredBuild.ts";
import useRequestConfigBuilder from "./useRequestConfigBuilder.ts";

export default function useDataLoadingBuilder<R extends TabularDataResponse | ApiReportResponse>(
  dispatch: React.Dispatch<ReducerAction>
) {
  const isAdding = useIsAddingFields();
  const dispatchGlobal = useDispatch();
  const { requestConfig } = useRequestConfigBuilder();
  const reportType = useSelector(selectReportType);
  const { buildTabularReport, buildCashFlowReport } = useEnhancedBiApiClientProvider();

  const [dataLoadingCancellationToken, setDataLoadingCancellationToken] = React.useState<CancellationToken | undefined>(
    undefined
  );
  const [failedMeasures, setFailedMeasures] = React.useState<FailedMeasure[]>([]);

  const requestGenerationRef = React.useRef(0);
  const cancellationRef = React.useRef(dataLoadingCancellationToken);
  useEffect(() => {
    cancellationRef.current = dataLoadingCancellationToken;
  }, [dataLoadingCancellationToken]);
  useEffect(
    () => () => {
      requestGenerationRef.current += 1;
      cancellationRef.current?.cancel();
    },
    []
  );
  const responseGenerationsRef = React.useRef(new WeakMap<object, number>());
  const cancelDataLoading = () => {
    requestGenerationRef.current += 1;
    dataLoadingCancellationToken?.cancel();
    dispatch({ type: GridDataStateActionType.LOADING_ENDED });
  };

  const loadData = (
    onBuildDone: (response: ApiResponse<R>) => void,
    onBuildError: (reason: string | undefined) => void
  ) => {
    dispatch({
      type: GridDataStateActionType.UPDATE_STATE,
      payload: { error: undefined },
    });
    dispatch({ type: GridDataStateActionType.LOADING_STARTED });
    dispatchGlobal(createCurrentReportAction("setEtag", { etag: undefined, cacheValid: undefined }));

    const calcConfig = { ...requestConfig };
    calcConfig.calcGroupTotals = true;

    const generation = ++requestGenerationRef.current;
    const buildReport = reportType === ReportType.CashFlow ? defined(buildCashFlowReport) : buildTabularReport;
    setDataLoadingCancellationToken(
      buildReport<typeof calcConfig, R>(
        calcConfig,
        (response) => {
          if (generation !== requestGenerationRef.current) {
            return;
          }
          responseGenerationsRef.current.set(response, generation);
          onBuildDone(response);
        },
        (error) => {
          if (generation === requestGenerationRef.current) {
            onBuildError(error);
          }
        }
      )
    );
  };

  const handleBuildDone = (resp: ApiResponse<R>) => {
    if (responseGenerationsRef.current.get(resp) !== requestGenerationRef.current) {
      return;
    }
    if (resp.data && isApiReportResponse(resp.data)) {
      dispatchGlobal(createApiReportAction("setResponse", resp.data.publicResponse));
    }
    dispatch({ type: GridDataStateActionType.LOADING_ENDED });
    if (resp.success) {
      if (resp.data.errorCode) {
        dispatch({
          type: GridDataStateActionType.UPDATE_STATE,
          payload: { error: resp.data.errorCode },
        });
      } else {
        dispatch({
          type: GridDataStateActionType.UPDATE_STATE,
          payload: {
            rows: resp.data.data || [],
            snapshot: resp.data.snapshot,
            groupTotals: resp.data.groupTotals,
            grandTotals: resp.data.grandTotals,
            totalCount: resp.data.snapshot?.detailCount ?? resp.data.totalCount,
            columns: resp.data.columns,
          },
        });
        dispatchGlobal(createDevToolsAction("update", { loggingItems: resp.data.loggingItems }));
        dispatchGlobal(createCurrentReportAction("setEtag", { etag: resp.data.etag, cacheValid: undefined }));

        setFailedMeasures(resp.data.failedMeasures);
      }
    } else {
      dispatch({
        type: GridDataStateActionType.UPDATE_STATE,
        payload: { error: resp.error?.message || "An error occurred while calculating report data. Please try again." },
      });
    }
  };

  const handleBuildError = (error: string | undefined) => {
    dispatch({ type: GridDataStateActionType.LOADING_ENDED });
    if (error === "canceled") {
      dispatch({
        type: GridDataStateActionType.UPDATE_STATE,
        payload: { error: undefined },
      });
    } else {
      dispatch({
        type: GridDataStateActionType.UPDATE_STATE,
        payload: { error: error || "An error occurred while calculating report data. Please try again." },
      });
    }
  };

  const handleInvalidConfiguration = () => {
    cancelCurrentAwaiter();
    cancelDataLoading();
    dispatchGlobal(createApiReportAction("setResponse", undefined));
  };

  const {
    doRequest: load,
    resetInitialRequestFlag,
    cancelCurrentAwaiter,
  } = useDeferredBuild({
    paused: isAdding,
    request: loadData,
    handleBuildDone,
    handleBuildError,
    cancelRequest: () => {
      cancelDataLoading();
    },
    onProgressChange: (progress) => dispatchGlobal(createCurrentReportAction("updateBuildProgress", progress)),
  });

  useEffect(() => {
    resetInitialRequestFlag();
  }, [requestConfig.reportId, resetInitialRequestFlag]);

  return {
    loadData: load,
    cancelDataLoading,
    failedMeasures,
    handleInvalidConfiguration,
  };
}

export type DataLoadingReturnType = ReturnType<typeof useDataLoadingBuilder>;

type CancellationToken = {
  cancel: () => void;
};
